import React from 'react'
import './CfoSite.css'
import { CfoBrandMark, CfoSiteFooter, navigateTo } from './CfoSiteShared'
import { SUBPROCESSORS } from './subprocessors'

type Doc = 'privacy' | 'terms'

const PRIVACY: { heading: string; body: string }[] = [
  { heading: 'What we access', body: 'When you connect a supported accounting platform, CFO Core reads your accounting data (accounts, transactions, and reports) through the provider’s API. We request read-only access and never write to or modify your accounting company.' },
  { heading: 'How we use it', body: 'We use your data solely to build and operate your CFO Core — financial statements, ratios, cash and AR analysis, forecasts, valuation, and presentations — for you and the people you share it with.' },
  { heading: 'Storage & security', body: 'Your data is stored in our secured cloud infrastructure. Provider access and refresh tokens are encrypted at rest. We do not sell your data or share it with advertisers. Xero data is excluded from shared model training and cross-company learning.' },
  { heading: 'Disconnecting', body: 'You can disconnect a provider at any time, which revokes our refresh access. Permanent deletion of imported company data is a separate action available from connection settings.' },
  { heading: 'AI connectors', body: 'You can connect FuseGrid to Claude, ChatGPT and FuseGrid for Excel. A connected app reaches only the companies you authorize, only with the permissions you grant, and never more than your current role in FuseGrid allows: access is rechecked on every request. We store only a hash of each connection token. If a connected app asks to verify who you are (for example ChatGPT workspaces that allow only their own email domain), FuseGrid shares your account email address and whether FuseGrid has verified it, and the connection screen says so before you approve. You can see, narrow and revoke every connection at /connect, and firm admins can revoke their members’ connections. What an AI app does with your data after FuseGrid returns it is governed by that provider’s terms.' },
  { heading: 'Contact', body: 'Questions about privacy? Email casey@fusegrid.io.' },
]

const TERMS: { heading: string; body: string }[] = [
  { heading: 'The service', body: 'CFO Core is provided by FuseGrid as a financial analysis product. QuickBooks Online is available and Xero is in private beta; Sage Intacct, Sage Accounting, and NetSuite are planned. Available features depend on provider permissions, reconciled data, and connection status. Xero GL detail and provider-specific AI features remain disabled until their required provider, legal, and security approvals pass.' },
  { heading: 'Your account', body: 'You’re responsible for keeping your login credentials secure and for the accuracy of the information you provide at signup.' },
  { heading: 'Not financial advice', body: 'CFO Core surfaces analysis derived from your books. It is informational and does not constitute accounting, tax, legal, or investment advice. Verify figures before relying on them for decisions.' },
  { heading: 'Availability', body: 'During early access the service is provided “as is,” without warranties, and may experience interruptions.' },
  { heading: 'Contact', body: 'Questions about these terms? Email casey@fusegrid.io.' },
]

export function CfoLegalPage({ doc }: { doc: Doc }) {
  const sections = doc === 'privacy' ? PRIVACY : TERMS
  const title = doc === 'privacy' ? 'Privacy Policy' : 'Terms of Service'
  return (
    <div className="cfo-site">
      <nav className="cfo-site-nav">
        <span style={{ cursor: 'pointer' }} onClick={() => navigateTo('/')}>
          <CfoBrandMark sub="by FuseGrid" />
        </span>
        <div className="cfo-site-nav-links">
          <button className="cfo-btn cfo-btn-primary" onClick={() => navigateTo('/signup')}>Connect your books</button>
        </div>
      </nav>
      <div className="cfo-wrap" style={{ padding: '48px 28px 64px', maxWidth: 760 }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 34 }}>{title}</h1>
        <div className="cfo-prose">
          {sections.map((s) => (
            <div key={s.heading}>
              <h2>{s.heading}</h2>
              <p>{s.body}</p>
            </div>
          ))}
          {doc === 'privacy' && (
            <div>
              <h2>Subprocessors</h2>
              <table className="cfo-legal-subprocessors">
                <thead><tr><th scope="col">Subprocessor</th><th scope="col">Purpose</th><th scope="col">Data</th></tr></thead>
                <tbody>{SUBPROCESSORS.map((s) => <tr key={s.name}><td>{s.name}</td><td>{s.purpose}</td><td>{s.data}</td></tr>)}</tbody>
              </table>
              <p>Questions or support? See <a onClick={() => navigateTo('/support')} style={{ cursor: 'pointer' }}>Support</a>.</p>
            </div>
          )}
        </div>
      </div>
      <CfoSiteFooter />
    </div>
  )
}

export default CfoLegalPage
